/**
 * Phase 21.4 Batch 3D — Enter Contest flow (/contests/:slug/enter).
 *
 * Auth-required. Loads the contest, fetches the user's existing tracks
 * or beats (based on entry_type), and lets them pick one to submit.
 * Explicitly reuses existing submissions/beats — no re-upload.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import SharedNavigation from "@/components/SharedNavigation";
import ContestStatusBadge from "@/components/contests/ContestStatusBadge";
import { canEnterContest } from "@/lib/contestStatus";
import { getCoverUrl } from "@/lib/mediaUrls";
import api from "@/lib/api";
import { normalizeSubmissionsList } from "@/lib/submissions";
import { toast } from "sonner";



const EnterContestPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [selectedRefId, setSelectedRefId] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // iter_206 — coin entry fee state
  const [coinBalance, setCoinBalance] = useState(null);
  const submitLockRef = (typeof window !== "undefined")
    ? (window.__enter_contest_lock = window.__enter_contest_lock || { current: false })
    : { current: false };

  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("session_token");

  useEffect(() => {
    if (!isAuthed) {
      navigate(`/login?next=${encodeURIComponent(`/contests/${slug}/enter`)}`);
    }
  }, [isAuthed, navigate, slug]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/contests/${slug}`);
      const c = r.data?.contest;
      setContest(c);
      if (!c) {
        setLoading(false);
        return;
      }
      // Fetch entries for cap check
      const my = await api.get(`/contests/${c.contest_id}/my-entry`);
      setMyEntries(my.data?.entries || []);
      // Fetch the appropriate item list based on entry_type
      if (c.entry_type === "track") {
        const s = await api.get("/submissions/my");
        // P0 · Feb 2026 — ``/submissions/my`` returns a paginated
        // envelope ``{ items, total, has_more, next_cursor, limit }``.
        // The normalizer accepts both the current envelope and the
        // legacy raw-array shape, and never returns a non-array so
        // ``items.map`` at render time cannot crash the screen.
        setItems(normalizeSubmissionsList(s.data));
      } else if (c.entry_type === "beat") {
        const b = await api.get("/beats/me/list");
        setItems(b.data?.beats || []);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "contests_disabled") {
        toast.error("Contests are not enabled yet");
        navigate("/contests");
        return;
      }
      toast.error("Failed to load contest");
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => {
    if (isAuthed) load();
  }, [isAuthed, load]);

  // iter_206 — Fetch coin balance so we can show the fee vs. balance panel.
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/wallet/me");
        const w = r.data?.wallet || r.data || {};
        const bal = w.balance ?? w.total ?? 0;
        if (!cancelled) setCoinBalance(bal);
      } catch {
        if (!cancelled) setCoinBalance(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthed]);

  const canEnter = useMemo(() => canEnterContest(contest), [contest]);
  const maxEntries = Number(contest?.max_entries_per_user || 1);
  const entryFee = Number(contest?.entry_fee_coins || 0);
  const insufficient = entryFee > 0 && coinBalance != null && coinBalance < entryFee;
  const activeEntries = myEntries.filter((e) => e.status !== "withdrawn");
  const atCap = activeEntries.length >= maxEntries;
  const usedRefIds = new Set(activeEntries.map((e) => e.ref_id));

  const submit = async () => {
    if (!selectedRefId) {
      toast.error("Please pick an item to enter");
      return;
    }
    // iter_206 — Client-side double-tap guard on top of server-side dedup.
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    try {
      await api.post(`/contests/${contest.contest_id}/entries`, {
        ref_id: selectedRefId,
        note,
      });
      toast.success("Entry submitted! Good luck 🏆");
      navigate(`/contests/${slug}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Entry failed";
      const detailCode = typeof detail === "object" ? detail.code : detail;
      if (detailCode === "already_entered") toast.error("You already entered this item — pick a different one.");
      else if (detailCode === "max_entries_reached") toast.error("You've hit the max entries for this contest.");
      else if (detailCode === "deadline_passed") toast.error("The deadline has passed — entries are closed.");
      else if (detailCode === "entry_fee_already_charged") toast.error("You've already been charged for this contest.");
      else if (detailCode === "insufficient_coins") {
        const need = typeof detail === "object" ? detail.required : null;
        toast.error(need ? `Not enough coins — need 🪙 ${need}. Grab more from the wallet.` : "Not enough coins.");
      } else toast.error(typeof detail === "string" ? detail : "Entry failed");
    } finally {
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  if (!isAuthed) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <p className="pt-24 px-4 text-gray-500" data-testid="enter-contest-loading">Loading…</p>
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <div className="max-w-3xl mx-auto pt-24 px-4 text-center" data-testid="enter-contest-not-found">
          <p className="text-5xl mb-3">🤔</p>
          <h1 className="text-2xl font-black mb-2">Contest not found</h1>
          <Link to="/contests" className="text-purple-300 underline">← Back to Contest Center</Link>
        </div>
      </div>
    );
  }

  const isTrack = contest.entry_type === "track";
  const emptyItemsMsg = isTrack
    ? "You don't have any tracks uploaded yet."
    : "You don't have any beats uploaded yet.";
  const uploadHref = isTrack ? "/submit" : "/submit-beat";
  const uploadLabel = isTrack ? "Upload a track" : "Upload a beat";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="enter-contest-page">
      <SharedNavigation />
      <div className="max-w-3xl mx-auto pt-24 px-4 pb-16 space-y-6">
        {/* Header */}
        <div>
          <Link to={`/contests/${slug}`} className="text-xs text-purple-300 hover:underline" data-testid="enter-contest-back">
            ← {contest.title}
          </Link>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <ContestStatusBadge contest={contest} />
            <span className="text-[10px] uppercase tracking-widest text-white/80 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 font-black">
              {isTrack ? "🎤 Track Entry" : "🎧 Beat Entry"}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mt-2" data-testid="enter-contest-title">Enter this contest</h1>
          <p className="text-xs text-gray-400 mt-1" data-testid="enter-contest-cap">
            Max entries per artist: <span className="text-white font-black">{maxEntries}</span>
            {` · You've used ${activeEntries.length}`}
          </p>
        </div>

        {/* Guard rails */}
        {!canEnter && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="enter-contest-closed">
            <p className="text-sm text-amber-100 font-black">This contest is not accepting entries right now.</p>
            <Link to={`/contests/${slug}`} className="text-xs text-amber-300 underline mt-2 inline-block">
              ← View contest
            </Link>
          </div>
        )}
        {canEnter && atCap && (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="enter-contest-at-cap">
            <p className="text-sm text-amber-100 font-black">
              You&apos;ve reached the max entries ({activeEntries.length}/{maxEntries}) for this contest.
            </p>
            <Link to="/contests/my" className="text-xs text-amber-300 underline mt-2 inline-block">
              View my submissions →
            </Link>
          </div>
        )}

        {canEnter && !atCap && (
          <>
            {/* iter_220 — Fee card promoted ABOVE the item picker so the
                artist sees the cost immediately, regardless of whether
                they have any tracks/beats yet. Insufficient balance
                gets a rose treatment + Get More Coins CTA. */}
            {entryFee > 0 && (
              <div
                className={`rounded-2xl border p-4 sm:p-5 flex items-center justify-between gap-3 ${
                  insufficient
                    ? "border-rose-500/40 bg-rose-500/10"
                    : "border-amber-500/40 bg-amber-500/10"
                }`}
                data-testid="enter-contest-fee-card-top"
              >
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">
                    🪙 Entry Fee
                  </p>
                  <p className="text-xl sm:text-2xl font-black text-white" data-testid="enter-contest-fee-amount-top">
                    🪙 {entryFee.toLocaleString()} RGM Coins
                  </p>
                  <p className="text-xs text-gray-200 mt-0.5">
                    Your balance:{" "}
                    <span
                      className={insufficient ? "text-rose-200 font-black" : "text-white font-black"}
                      data-testid="enter-contest-balance-top"
                    >
                      🪙 {coinBalance != null ? coinBalance.toLocaleString() : "—"}
                    </span>
                  </p>
                </div>
                {insufficient && (
                  <Link
                    to="/wallet"
                    className="px-3 py-2 rounded-lg bg-amber-500 text-black font-black text-xs hover:bg-amber-400 shrink-0"
                    data-testid="enter-contest-get-coins-top"
                  >
                    Get More Coins →
                  </Link>
                )}
              </div>
            )}
            {/* Item picker */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <h2 className="text-lg font-black">Pick {isTrack ? "a track" : "a beat"} to enter</h2>
                <Link
                  to={uploadHref}
                  className="text-xs text-purple-300 hover:underline"
                  data-testid="enter-contest-upload-link"
                >
                  {uploadLabel} →
                </Link>
              </div>

              {items.length === 0 ? (
                <div className="text-center py-8" data-testid="enter-contest-no-items">
                  <p className="text-4xl mb-2">📼</p>
                  <p className="text-sm text-white font-black">{emptyItemsMsg}</p>
                  <p className="text-xs text-gray-400 mt-1">Upload one first, then come back to enter.</p>
                  <Link
                    to={uploadHref}
                    className="inline-flex items-center mt-3 px-4 py-2 rounded-full bg-white text-black text-xs font-black hover:bg-amber-200"
                  >
                    {uploadLabel}
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="enter-contest-items-grid">
                  {/* P0 · Feb 2026 — belt-and-braces guard: state is
                      already normalized to an array by ``setItems`` /
                      ``normalizeSubmissionsList``; this second check
                      protects the render path even if a future state
                      mutation ever slips in a non-array value. */}
                  {(Array.isArray(items) ? items : []).map((item) => {
                    const refId = isTrack ? item.submission_id : item.beat_id;
                    const title = isTrack ? (item.track_title || "Untitled") : (item.title || "Untitled");
                    const cover = isTrack ? getCoverUrl(item) : (item.cover_url || null);
                    const already = usedRefIds.has(refId);
                    const selected = selectedRefId === refId;
                    return (
                      <button
                        key={refId}
                        type="button"
                        disabled={already}
                        onClick={() => setSelectedRefId(refId)}
                        className={`flex items-center gap-3 p-2.5 rounded-xl border transition-all text-left ${
                          selected
                            ? "border-amber-500/70 bg-amber-500/10"
                            : already
                              ? "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                              : "border-white/10 bg-black/40 hover:border-purple-500/40 hover:bg-white/5"
                        }`}
                        data-testid={`enter-contest-item-${refId}`}
                      >
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                          {cover ? (
                            <img src={cover} alt={title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-2xl">🎵</div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-black truncate">{title}</p>
                          <p className="text-[11px] text-gray-400">
                            {already ? "Already entered" : selected ? "✓ Selected" : "Tap to select"}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Note + submit */}
            {items.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5 space-y-3">
                <label className="text-[11px] uppercase tracking-widest text-gray-400 font-black block">
                  Optional note to judges
                </label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything the judges should know? (optional)"
                  className="bg-white/5 border-white/10 text-white min-h-[80px]"
                  data-testid="enter-contest-note"
                  maxLength={1000}
                />
                <p className="text-[10px] text-gray-500">{note.length}/1000</p>
                {entryFee > 0 && (
                  <div
                    className={`rounded-xl border p-3 flex items-center justify-between gap-3 ${
                      insufficient
                        ? "border-rose-500/40 bg-rose-500/10"
                        : "border-amber-500/40 bg-amber-500/10"
                    }`}
                    data-testid="enter-contest-fee-card"
                  >
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">
                        Entry Fee
                      </p>
                      <p className="text-lg font-black text-white" data-testid="enter-contest-fee-amount">
                        🪙 {entryFee} RGM Coins
                      </p>
                      <p className="text-xs text-gray-300">
                        Your balance:{" "}
                        <span data-testid="enter-contest-balance">
                          🪙 {coinBalance != null ? coinBalance : "—"}
                        </span>
                      </p>
                    </div>
                    {insufficient && (
                      <Link
                        to="/wallet"
                        className="px-3 py-2 rounded-lg bg-amber-500 text-black font-black text-xs hover:bg-amber-400"
                        data-testid="enter-contest-get-coins"
                      >
                        Get More Coins →
                      </Link>
                    )}
                  </div>
                )}
                <Button
                  onClick={submit}
                  disabled={!selectedRefId || submitting || insufficient}
                  className="bg-white text-black hover:bg-amber-200 font-black w-full sm:w-auto px-6 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="enter-contest-submit"
                >
                  {submitting
                    ? "Submitting…"
                    : entryFee > 0
                    ? `Submit entry — 🪙 ${entryFee}`
                    : "Submit entry 🏆"}
                </Button>
                <p className="text-[10px] text-gray-500">
                  By entering you confirm this is your own work and follows the contest rules.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EnterContestPage;
